/**
 * Application Configuration
 * Centralizes all environment variables and configuration
 */

export const config = {
  app: {
    name: import.meta.env.VITE_APP_NAME || 'FlightCents',
    url: import.meta.env.VITE_APP_URL || 'https://www.flightcents.com',
    env: import.meta.env.VITE_APP_ENV || import.meta.env.MODE || 'development',
    isDevelopment: import.meta.env.DEV,
    isProduction: import.meta.env.PROD,
  },

  api: {
    url: import.meta.env.VITE_API_URL || 'http://localhost:3001',
    timeout: parseInt(import.meta.env.VITE_API_TIMEOUT || '30000', 10),
    mockEnabled: import.meta.env.VITE_MOCK_API === 'true',
  },

  analytics: {
    gaTrackingId: import.meta.env.VITE_GA_TRACKING_ID || '',
    enabled: import.meta.env.PROD,
  },

  auth: {
    // Supabase
    supabase: {
      url: import.meta.env.VITE_SUPABASE_URL || '',
      anonKey: import.meta.env.VITE_SUPABASE_ANON_KEY || '',
    },
    // Clerk
    clerk: {
      publishableKey: import.meta.env.VITE_CLERK_PUBLISHABLE_KEY || '',
    },
    // Auth0
    auth0: {
      domain: import.meta.env.VITE_AUTH0_DOMAIN || '',
      clientId: import.meta.env.VITE_AUTH0_CLIENT_ID || '',
      audience: import.meta.env.VITE_AUTH0_AUDIENCE || '',
    },
  },

  payments: {
    paddle: {
      vendorId: import.meta.env.VITE_PADDLE_VENDOR_ID || '',
      clientToken: import.meta.env.VITE_PADDLE_CLIENT_TOKEN || '',
      sandbox: import.meta.env.VITE_PADDLE_SANDBOX === 'true',
      priceIdPro: import.meta.env.VITE_PADDLE_PRICE_ID_PRO || '',
      priceIdPremium: import.meta.env.VITE_PADDLE_PRICE_ID_PREMIUM || '',
    },
  },

  upload: {
    maxSize: parseInt(import.meta.env.VITE_UPLOAD_MAX_SIZE || '5242880', 10), // 5MB default
    allowedTypes: (import.meta.env.VITE_UPLOAD_ALLOWED_TYPES || 'image/jpeg,image/png,application/pdf').split(','),
  },

  email: {
    from: import.meta.env.VITE_EMAIL_FROM || 'noreply@flightcents.com',
    support: import.meta.env.VITE_EMAIL_SUPPORT || 'support@flightcents.com',
  },

  sentry: {
    dsn: import.meta.env.VITE_SENTRY_DSN || '',
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || 'development',
    tracesSampleRate: parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || '0.1'),
    enabled: !!import.meta.env.VITE_SENTRY_DSN && import.meta.env.PROD,
  },

  features: {
    blog: import.meta.env.VITE_FEATURE_BLOG !== 'false',
    affiliate: import.meta.env.VITE_FEATURE_AFFILIATE !== 'false',
    premium: import.meta.env.VITE_FEATURE_PREMIUM === 'true',
    flightSchools: import.meta.env.VITE_FEATURE_FLIGHT_SCHOOLS !== 'false',
    benchmarking: import.meta.env.VITE_FEATURE_BENCHMARKING !== 'false',
  },

  debug: {
    enabled: import.meta.env.VITE_DEBUG_MODE === 'true',
  },
} as const;

/**
 * Validate required environment variables
 */
export function validateConfig() {
  const errors: string[] = [];

  // In production, ensure critical values are set
  if (config.app.isProduction) {
    if (!config.app.url || config.app.url.includes('localhost')) {
      errors.push('VITE_APP_URL must be set to production URL');
    }

    if (config.features.premium && !config.payments.paddle.vendorId) {
      errors.push('VITE_PADDLE_VENDOR_ID is required when premium features are enabled');
    }
  }

  if (errors.length > 0) {
    console.error('Configuration validation errors:', errors);
    throw new Error(`Configuration validation failed:\n${errors.join('\n')}`);
  }

  return true;
}

export default config;
